import { useId } from "react";
import styled from "styled-components";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import { TimePoint } from "../../../utils/partnerMetrics";

const Headline = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
`;

const HeadValue = styled.span`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 26px;
  font-weight: 800;
  color: #111111;
  line-height: 1;
`;

const HeadCaption = styled.span`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  color: #8a7f74;
`;

const Empty = styled.div`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13.5px;
  color: #9a8e84;
  padding: 60px 0;
  text-align: center;
`;

const TipBox = styled.div`
  background: #111111;
  color: #ffffff;
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
`;

interface TrendChartProps {
  title: string;
  data: TimePoint[];
  loading?: boolean;
  color?: string;
  valueSuffix?: string;
  valuePrefix?: string;
  formatValue?: (value: number) => string;
  caption?: string;
  info?: string;
  emptyMessage?: string;
  height?: number;
}

const TrendChart = ({
  title,
  data,
  loading,
  color = "#1f2937",
  valueSuffix = "",
  valuePrefix = "",
  formatValue,
  caption,
  info,
  emptyMessage = "Not enough data yet to chart a trend.",
  height = 220,
}: TrendChartProps) => {
  const gradientId = `trend-${useId().replace(/:/g, "")}`;

  if (loading) {
    return (
      <Panel title={title} info={info}>
        <Skeleton $w="30%" $h="24px" $radius="8px" $mb="16px" />
        <Skeleton $w="100%" $h={`${height}px`} $radius="12px" />
      </Panel>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Panel title={title} info={info}>
        <Empty>{emptyMessage}</Empty>
      </Panel>
    );
  }

  const latest = data[data.length - 1].value;
  const display = (v: number) =>
    formatValue ? formatValue(v) : `${valuePrefix}${v.toLocaleString()}${valueSuffix}`;

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload as TimePoint;
    return (
      <TipBox>
        {p.label}: {display(p.value)}
      </TipBox>
    );
  };

  return (
    <Panel title={title} info={info}>
      <Headline>
        <HeadValue>
          {display(latest)}
        </HeadValue>
        {caption && <HeadCaption>{caption}</HeadCaption>}
      </Headline>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.18} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe4" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "#9a8e84", fontFamily: "Aileron, Poppins, sans-serif" }}
            axisLine={{ stroke: "#e7e0d8" }}
            tickLine={false}
          />
          <YAxis
            width={valuePrefix ? 48 : 34}
            tickFormatter={(v: number) => `${valuePrefix}${v.toLocaleString()}`}
            tick={{ fontSize: 11, fill: "#9a8e84", fontFamily: "Aileron, Poppins, sans-serif" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={renderTooltip} cursor={{ stroke: "#d4ccc4" }} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2.5}
            fill={`url(#${gradientId})`}
            dot={{ r: 3, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Panel>
  );
};

export default TrendChart;
